import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于我们 - 亲亲漫画网 | 品牌介绍与联系方式';
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于亲亲漫画网',
            description: '亲亲漫画网品牌介绍、口碑评价与联系方式，了解我们的漫画阅读平台。',
            url: 'https://yutuocq.cn/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '亲亲漫画网',
              url: 'https://yutuocq.cn',
            },
          }),
        }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              关于亲亲漫画网
            </h1>
            <p className="text-sm text-foreground-500 max-w-2xl mx-auto">
              致力于为广大<strong>漫画</strong>爱好者提供<strong>下拉式完整版免费漫画阅读</strong>体验
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
            <div>
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">品牌介绍</h2>
              <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                <p>
                  <strong>亲亲漫画网</strong>成立于2024年，是一个专注于提供<strong>下拉式完整版免费漫画阅读</strong>的在线漫画平台。我们理解漫画爱好者的需求——不仅需要丰富的漫画资源，更需要流畅舒适的阅读体验。
                </p>
                <p>
                  平台主打<strong>无删减韩国漫画</strong>资源，涵盖韩国恋爱、韩国奇幻、韩国动作、韩国剧情等多种类型。同时，我们也有丰富的日本漫画和国产漫画资源，满足不同读者的口味偏好。
                </p>
                <p>
                  <strong>下拉观看</strong>是我们最核心的阅读体验设计。传统翻页模式常常打断阅读节奏，而下拉式阅读让读者只需轻轻一滑，就能沉浸在连续不断的漫画故事中，不卡顿、不中断，让每一部漫画的精彩剧情一气呵成。
                </p>
                <p>
                  我们承诺所有漫画均为<strong>无删减完整版</strong>，尊重创作者的原始内容和每一位读者的阅读权利。因为热爱漫画，所以用心做好每一个细节。
                </p>
              </div>
            </div>

            <div>
              <div className="rounded-lg overflow-hidden h-72 md:h-80 bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20cozy%20reading%20nook%20with%20a%20tablet%20displaying%20colorful%20manga%20pages%20surrounded%20by%20warm%20ambient%20lighting%20soft%20blanket%20and%20a%20cup%20of%20tea%20on%20a%20wooden%20side%20table%20pastel%20color%20palette%20comfortable%20and%20inviting%20atmosphere%20clean%20composition&width=800&height=500&seq=about-manga-reading&orientation=landscape"
                  alt="亲亲漫画网 - 舒适的下拉式漫画阅读体验"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          <section className="mb-16">
            <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6 text-center">我们的优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  icon: 'ri-book-open-line',
                  title: '海量资源',
                  desc: '超过12,000部漫画资源，涵盖韩国、日本、国产等多国漫画，持续更新中',
                },
                {
                  icon: 'ri-download-cloud-line',
                  title: '无删减完整版',
                  desc: '所有漫画均为无删减版本，保留原作完整内容，尊重创作者与读者',
                },
                {
                  icon: 'ri-smartphone-line',
                  title: '下拉式阅读',
                  desc: '流畅的下拉观看模式，无需翻页，一气呵成的沉浸式阅读体验',
                },
                {
                  icon: 'ri-money-dollar-circle-line',
                  title: '完全免费',
                  desc: '零费用、零门槛，所有资源免费阅读，无需注册即可畅享漫画世界',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6 text-center">读者口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '漫画迷小樱',
                  rating: 5,
                  text: '亲亲漫画网真的太棒了！下拉式阅读体验超级流畅，韩国漫画资源也特别全，再也不用到处找漫画了。',
                },
                {
                  name: '二次元阿星',
                  rating: 5,
                  text: '无删减这一点就值得五星好评！很多平台的漫画都有删减，只有这里能看到完整版，画质也很清晰。',
                },
                {
                  name: '熬夜冠军',
                  rating: 5,
                  text: '自从发现了亲亲漫画网，每天晚上都忍不住熬夜看漫画！下拉观看太方便了，剧情根本停不下来。',
                },
              ].map((review, index) => (
                <div
                  key={index}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">"{review.text}"</p>
                  <p className="text-xs font-semibold text-foreground-900">{review.name}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6 text-center">联系我们</h2>
            <div className="max-w-2xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">contact@yutuocq.cn</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-qq-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服QQ</h3>
                  <p className="text-xs text-foreground-500">1234567890</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                  <p className="text-xs text-foreground-500">每天 9:00 - 21:00</p>
                </div>
              </div>

              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">版权声明</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  亲亲漫画网所有漫画资源均来自互联网公开渠道，仅供读者学习交流使用。如涉及版权问题，请版权方及时联系我们，我们将第一时间处理。我们支持正版，鼓励读者在条件允许的情况下支持原作者和正版平台。
                </p>
              </div>
            </div>
          </section>
        </section>
      </main>
      <Footer />
    </div>
  );
}